import React from 'react';
import { CButton, CModal, CModalBody, CModalHeader, CModalTitle, CModalFooter, CAlert } from '@coreui/react';
import _t from 'counterpart';
import { useMutation, useQueryClient } from 'react-query';
import { extractErrorMessage } from '../helpers';
import ButtonWithLoader from './ButtonWithLoader';
import toast from 'react-hot-toast';

interface IProps {
  id: string;
  show: boolean;
  hidePanel: Function;
  onConfirm: Function;
  title: string;
  description?: string;
  error: string | null;
  buttonColor: string;
  withMutation: boolean;
  queryCacheKeyToInvalidate?: string;
  isLoading?: boolean;
}

const ConfirmationModal = ({
  id,
  show,
  hidePanel,
  onConfirm,
  title,
  description,
  error,
  buttonColor,
  withMutation,
  queryCacheKeyToInvalidate,
  isLoading = false,
}: IProps) => {
  const queryClient = useQueryClient();
  const mutation = useMutation(['mutation'], () => onConfirm(id), {
    onSuccess: () => {
      hidePanel(true);
      if (queryCacheKeyToInvalidate) {
        queryClient.invalidateQueries(queryCacheKeyToInvalidate);
      }
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const confirm = () => {
    if (withMutation) {
      mutation.mutate();
    } else {
      onConfirm();
    }
  };

  const loading = withMutation ? mutation.isLoading : isLoading;

  return (
    <CModal show={show} onClose={hidePanel} size="sm">
      <CModalHeader className={`d-flex flex-column ${description ? 'pb-1' : ''}`}>
        {error && <CAlert color="danger">{error}</CAlert>}
        <CModalTitle> {title}</CModalTitle>
      </CModalHeader>
      <CModalBody className={description ? 'pt-1' : ''}>
        {description && <p className="text-center mb-0">{description}</p>}
      </CModalBody>
      <CModalFooter className="modal-footer--alternative">
        <ButtonWithLoader
          isLoading={loading}
          onClick={confirm}
          buttonColor={buttonColor}
          spinnerColor="secondary"
          title={_t.translate('global.confirm')}
        />
        <CButton color="light" variant="outline" onClick={hidePanel} disabled={loading}>
          {_t('global.cancel')}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

ConfirmationModal.defaultProps = {
  queryCacheKeyToInvalidate: undefined,
};

export default ConfirmationModal;
